import api from './api';
import cache from '../utils/cache';
import requestQueue, { RequestPriority } from '../utils/request-queue';

export interface Category {
  id: string;
  name_french: string;
  name_english: string;
  subcategories?: Subcategory[];
}

export interface Subcategory {
  id: string;
  category_id: string;
  name_french: string;
  name_english: string;
  fields?: Field[];
}

export interface Field {
  id: number;
  subcategory_id: string;
  name_french: string;
  name_english: string;
}

export interface CreateCategoryDto {
  name_french: string;
  name_english: string;
}

export interface CreateSubcategoryDto {
  category_id: string;
  name_french: string;
  name_english: string;
}

export interface CreateFieldDto {
  subcategory_id: string;
  name_french: string;
  name_english: string;
}

export interface UpdateCategoryDto {
  name_french?: string;
  name_english?: string;
}

export interface UpdateSubcategoryDto {
  category_id?: string;
  name_french?: string;
  name_english?: string;
}

export interface UpdateFieldDto {
  subcategory_id?: string;
  name_french?: string;
  name_english?: string;
}

class CategoriesService {
  // Categories
  async getCategories(): Promise<Category[]> {
    const cacheKey = 'categories_all';
    const cached = cache.get<Category[]>(cacheKey);

    if (cached) {
      return cached;
    }

    const response = await requestQueue.enqueue(
      () => api.get('/categories'),
      RequestPriority.NORMAL
    );

    // Cache for 5 minutes
    cache.set(cacheKey, response.data, 5 * 60 * 1000);
    return response.data;
  }

  async getCategory(id: string): Promise<Category> {
    const response = await api.get(`/categories/${id}`);
    return response.data;
  }

  async createCategory(data: CreateCategoryDto): Promise<Category> {
    const response = await api.post('/categories', data);
    // Invalidate categories cache
    cache.invalidate('categories_all');
    return response.data;
  }

  async updateCategory(id: string, data: UpdateCategoryDto): Promise<Category> {
    const response = await api.patch(`/categories/${id}`, data);
    cache.invalidate('categories_all');
    return response.data;
  }

  async deleteCategory(id: string): Promise<void> {
    await api.delete(`/categories/${id}`);
    cache.invalidate('categories_all');
  }

  // Subcategories
  async getSubcategories(): Promise<Subcategory[]> {
    const response = await api.get('/subcategories');
    return response.data;
  }

  async getSubcategory(id: string): Promise<Subcategory> {
    const response = await api.get(`/subcategories/${id}`);
    return response.data;
  }

  async getSubcategoriesByCategory(categoryId: string): Promise<Subcategory[]> {
    const cacheKey = `subcategories_${categoryId}`;
    const cached = cache.get<Subcategory[]>(cacheKey);

    if (cached) {
      return cached;
    }

    const response = await requestQueue.enqueue(
      () => api.get(`/subcategories/category/${categoryId}`),
      RequestPriority.NORMAL
    );

    // Cache for 5 minutes
    cache.set(cacheKey, response.data, 5 * 60 * 1000);
    return response.data;
  }

  async createSubcategory(data: CreateSubcategoryDto): Promise<Subcategory> {
    const response = await api.post('/subcategories', data);
    return response.data;
  }

  async updateSubcategory(id: string, data: UpdateSubcategoryDto): Promise<Subcategory> {
    const response = await api.patch(`/subcategories/${id}`, data);
    return response.data;
  }

  async deleteSubcategory(id: string): Promise<void> {
    await api.delete(`/subcategories/${id}`);
  }

  // Fields
  async getFields(): Promise<Field[]> {
    const response = await api.get('/fields');
    return response.data;
  }

  async getField(id: number): Promise<Field> {
    const response = await api.get(`/fields/${id}`);
    return response.data;
  }

  async getFieldsBySubcategory(subcategoryId: string): Promise<Field[]> {
    const cacheKey = `fields_${subcategoryId}`;
    const cached = cache.get<Field[]>(cacheKey);

    if (cached) {
      return cached;
    }

    const response = await requestQueue.enqueue(
      () => api.get(`/fields/subcategory/${subcategoryId}`),
      RequestPriority.NORMAL
    );

    // Cache for 5 minutes
    cache.set(cacheKey, response.data, 5 * 60 * 1000);
    return response.data;
  }

  async createField(data: CreateFieldDto): Promise<Field> {
    const response = await api.post('/fields', data);
    // Invalidate fields cache for this subcategory
    cache.invalidatePattern(/^fields_/);
    return response.data;
  }

  async updateField(id: number, data: UpdateFieldDto): Promise<Field> {
    const response = await api.patch(`/fields/${id}`, data);
    cache.invalidatePattern(/^fields_/);
    return response.data;
  }

  async deleteField(id: number): Promise<void> {
    await api.delete(`/fields/${id}`);
    cache.invalidatePattern(/^fields_/);
  }

  // Admin dashboard data
  async getAdminDashboardData() {
    const response = await api.get('/admin/dashboard');
    return response.data;
  }

  async getHierarchy() {
    const response = await api.get('/admin/hierarchy');
    return response.data;
  }
}

export default new CategoriesService();
